// COP — useSubscription hook (Wave 2C.2).
//
// Single source of truth for the signed-in workspace's billing state. Other
// surfaces (Account billing tab, future Settings → Facturación, dashboard
// banners for trial countdown / past-due warnings) all read from this hook so
// the row is fetched once per session and cached by React Query.
//
// Shape:
//   useSubscription() → {
//     subscription: Subscription | null,   // null while loading or for
//                                          // unauthenticated callers
//     plan:         Plan | null,           // joined plan, same lifecycle
//     isLoading:    boolean,
//     isError:      boolean,
//     refetch:      () => void,
//   }
//
// Implementation notes:
//   * The `subscriptions` row has `workspace_id = auth.uid()` (the trial
//     trigger from migration 20260513180000 always inserts that mapping).
//     RLS scopes SELECT to `workspace_id = auth.uid() OR is_super_admin`, so
//     a normal user only ever sees their own row.
//   * The supabase-generated `Database` interface in
//     `src/integrations/supabase/types.ts` does not yet include `plans` /
//     `subscriptions` (Sergio re-generates types after Wave 2B migration
//     lands). We cast the client through `unknown` here so the call type-
//     checks today — exact same pattern used in PlansAdmin.tsx and
//     useAuth.tsx for the same reason.
//   * We deliberately request the join with `plan:plan_id(*)`. PostgREST
//     returns the joined row under the alias key `plan` so the consumer
//     reads `subscription.plan` directly. We then split the row into
//     `subscription` (without the nested plan) and `plan` for ergonomic
//     access.

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import type { Plan, Subscription } from '@/types';

/** PostgREST row shape returned by the joined SELECT. */
interface SubscriptionWithPlanRow extends Subscription {
  plan: Plan | null;
}

interface UseSubscriptionResult {
  subscription: Subscription | null;
  plan: Plan | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Fetches the caller's active subscription joined with the plan. Returns
 * `null` for both rows when there's no signed-in user — callers should
 * treat this as "still loading" or "anonymous viewer".
 */
async function fetchSubscriptionForUser(
  userId: string,
): Promise<SubscriptionWithPlanRow | null> {
  // Cast through `unknown` because the generated `Database` type doesn't yet
  // know about `subscriptions`. Mirrors the cast in PlansAdmin.tsx.
  const client = supabase as unknown as {
    from: (t: string) => {
      select: (cols: string) => {
        eq: (col: string, value: unknown) => {
          single: () => Promise<{
            data: SubscriptionWithPlanRow | null;
            error: unknown;
          }>;
        };
      };
    };
  };
  const { data, error } = await client
    .from('subscriptions')
    .select('*, plan:plan_id(*)')
    .eq('workspace_id', userId)
    .single();
  if (error) {
    // `PGRST116` = no rows. We surface that as a soft null (the trial-on-
    // signup trigger should always produce a row, but a paranoid fallback
    // keeps the UI from spamming Sentry on stale local dev DBs).
    const errCode = (error as { code?: string } | null)?.code;
    if (errCode === 'PGRST116') {
      return null;
    }
    throw error;
  }
  return data;
}

export function useSubscription(): UseSubscriptionResult {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: ['my-subscription', userId],
    enabled: !!userId,
    queryFn: () => {
      if (!userId) return Promise.resolve(null);
      return fetchSubscriptionForUser(userId);
    },
    staleTime: 1000 * 30,
  });

  const row = query.data ?? null;
  const plan = row?.plan ?? null;
  // Strip the joined plan from the subscription object so consumers that
  // expect a flat `Subscription` shape don't trip over the extra key.
  const subscription = row
    ? (() => {
        const { plan: _unused, ...rest } = row;
        void _unused;
        return rest as Subscription;
      })()
    : null;

  return {
    subscription,
    plan,
    isLoading: authLoading || query.isLoading || query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export default useSubscription;
